/* eslint-disable react/no-multi-comp */
import React, { useEffect, useMemo, useState } from 'react';
import Lottie from 'lottie-react';
import { BackIcon, ContractQRIcon } from 'assets/icons';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamCustomersPermissions } from 'containers/app/appSlice';
import { useGetInvoiceQRImageMutation, useLazyGetInvoiceByIdV2Query } from 'containers/invoices/api';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { VoidFn, clevertapEvents } from 'types/baseTypes';
import { ShareReceiptMainSection } from 'containers/invoices/components/ShareReceiptMainSection';
import { InvoiceByIdV2, ShareReceiptPageState } from 'containers/invoices/invoices.model';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { logAnalyticEvent } from 'utils/analytics';
import { constructArrayObjectAsString } from 'utils/commonUtils';
import routesPath from 'routes/RoutesPath';
import SidePanel from 'components/side-panel/SidePanel';
import { SidePanelRouteState } from 'routes/types';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { SharePanelType } from '../types';
import { useLazyGetReceiptByIdQuery } from 'containers/receipts/api';

interface ShareReceiptPanelProps {
  resetReceiptsQuery?: VoidFn;
  onClose?: VoidFn;
  type?: 'INVOICE' | 'RECEIPT';
}

interface OutletContext {
  handleSidePanelClose: VoidFn;
  resetInvoicesQuery: VoidFn;
  resetReceiptsQuery: VoidFn;
}

const ShareReceiptPanel: React.FC<ShareReceiptPanelProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<ShareReceiptPageState>(ShareReceiptPageState.MAIN);
  const [isRootState, setIsRootState] = useState(true);

  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const currentTeam = useAppSelector(selectCurrentTeam);

  const { isInvoice, isRefund, isReceipt, panelType } = useMemo(() => {
    const pathname = location?.pathname || '';
    // Check refund first since 'share-refund-receipt' contains 'share-receipt'
    const refund = pathname.includes('share-refund-receipt');
    const invoice = pathname.includes('share-invoice');
    const receipt = !refund && pathname.includes('share-receipt');

    let panelTypeValue: SharePanelType;
    if (invoice) {
      panelTypeValue = SharePanelType.Invoice;
    } else if (refund) {
      panelTypeValue = SharePanelType.Refund;
    } else {
      panelTypeValue = SharePanelType.Receipt;
    }

    return {
      isInvoice: invoice,
      isRefund: refund,
      isReceipt: receipt,
      panelType: panelTypeValue
    };
  }, [location.pathname]);
  const [getInvoiceByIdQuery, getInvoiceByIdState] = useLazyGetInvoiceByIdV2Query();
  const {
    data: invoiceDataResponse,
    isSuccess: invoiceDataSuccess,
    isFetching: fetchingInvoiceData
  } = getInvoiceByIdState;
  const invoiceData: InvoiceByIdV2 = invoiceDataResponse?.invoice;

  const [getReceiptByIdQuery, getReceiptByIdState] = useLazyGetReceiptByIdQuery();
  const { data: receiptData, isSuccess: receiptDataSuccess, isFetching: fetchingReceiptData } = getReceiptByIdState;

  const [getInvoiceQRImage, { data: qrImageData, isLoading: qrImageLoading }] = useGetInvoiceQRImageMutation();

  const { rootInvoiceId, invoiceId: invoiceIdFromUrl, receiptId, rootReceiptId } = useParams();
  const { handleSidePanelClose, resetReceiptsQuery } = useOutletContext<OutletContext>();

  useEffect(() => {
    if ((invoiceIdFromUrl || rootInvoiceId) && isInvoice && currentTeam?.id) {
      const currentInvoiceId = invoiceIdFromUrl || rootInvoiceId;
      getInvoiceByIdQuery({ invoiceId: currentInvoiceId, teamId: currentTeam.id });
      if (!qrImageData?.qrImage) {
        getInvoiceQRImage({
          teamId: currentTeam.id.toString(),
          invoiceId: currentInvoiceId
        });
      }
    }
  }, [
    invoiceIdFromUrl,
    rootInvoiceId,
    currentTeam,
    isInvoice,
    getInvoiceByIdQuery,
    getInvoiceQRImage,
    qrImageData?.qrImage
  ]);

  useEffect(() => {
    if ((receiptId || rootReceiptId) && (isRefund || isReceipt) && currentTeam?.id) {
      getReceiptByIdQuery({ receiptId: receiptId, teamId: currentTeam.id });
    }
  }, [receiptId, rootReceiptId, currentTeam, isRefund, isReceipt, getReceiptByIdQuery]);

  useEffect(() => {
    let routeStateTimeout;
    const routeState: SidePanelRouteState = location.state ?? {};
    if (routeState?.fromSidePanel) {
      setIsRootState(false);
    }
    if (routeState?.shouldAnimate) {
      routeStateTimeout = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
      }, 800);
    }
    return () => {
      clearTimeout(routeStateTimeout);
    };
  }, [location]);

  const getEventName = () => {
    if (location.pathname === routesPath.RECEIPTS) return clevertapEvents.webReceiptsListSharePanelOpen;
    return clevertapEvents.WebInvoicesListSharePanelOpen;
  };

  useEffect(() => {
    if (invoiceDataSuccess) {
      logAnalyticEvent(getEventName(), {
        'Line Items': constructArrayObjectAsString(invoiceData.lineItems, 'Line Item'),
        Page: panelType
      });
    }
  }, [invoiceDataSuccess, panelType]);

  useEffect(() => {
    if (
      !customerPermissions.editCustomersCreatedByTeam &&
      !customerPermissions.editExistingCustomers &&
      !customerPermissions.viewCustomersCreatedByTeam
    ) {
      handleSidePanelClose();
    }
  }, [customerPermissions]);

  const handleSidePanelBack = () => {
    if (isRootState) {
      handleSidePanelClose();
    } else {
      navigate(-1);
    }
  };

  const getQRData = () => {
    if (isInvoice) return qrImageData?.qrImage;
    return receiptData.qr;
  };

  const getAmount = () => {
    if (isRefund || isReceipt) return getAmountWithCurrency(receiptData.amount);
    return getAmountWithCurrency(invoiceData.totalAmount);
  };

  const ExpandedQRCode = () => {
    return (
      <div className="flex h-full flex-col">
        <div className="flex flex-row items-center p-5">
          <div onClick={() => setPageState(ShareReceiptPageState.MAIN)} className="cursor-pointer">
            <BackIcon />
          </div>
          <div className="pl-2 text-base font-bold text-primaryText">{'Back'}</div>
        </div>
        <div className="flex h-full w-full flex-1 flex-col items-center justify-center">
          <div className={'text-base font-semibold'}>Scan with camera</div>
          <div className={'text-xs font-normal'}>Or use a QR code scanning app</div>
          <div className="flex flex-col items-center">
            <img
              src={getQRData()}
              className={'w-60 cursor-pointer'}
              alt={'qr-code'}
              onClick={() => setPageState(ShareReceiptPageState.MAIN)}
            />
            <div className={'mb-7 text-3xl font-bold text-secondary'}>{getAmount()}</div>
            <div className="flex flex-row items-center">
              <ContractQRIcon />
              <div className={'ml-2 text-sm font-semibold text-tertiaryText'}>Tap the QR to shrink</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
      <div className="h-full w-96">
        {fetchingInvoiceData || fetchingReceiptData || (isInvoice && qrImageLoading) ? (
          <div className="flex h-full w-full items-center justify-center">
            <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
          </div>
        ) : (
          (invoiceDataSuccess || receiptDataSuccess) && (
            <section
              className={`delay-400 customNormalScroll right-0 h-full w-full overflow-auto bg-white 
           shadow-xl transition-all duration-500 ease-in-out`}>
              {pageState === ShareReceiptPageState.MAIN ? (
                <ShareReceiptMainSection
                  onClose={handleSidePanelBack}
                  invoiceData={invoiceData}
                  receiptData={receiptData}
                  type={panelType}
                  setPageState={setPageState}
                  isRootState={isRootState}
                  resetReceiptsQuery={resetReceiptsQuery}
                  receiptId={receiptId ? receiptId : rootReceiptId}
                  qrData={getQRData()}
                />
              ) : (
                <ExpandedQRCode />
              )}
            </section>
          )
        )}
      </div>
    </SidePanel>
  );
};

export default ShareReceiptPanel;
